import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { EnhancedTaskItemExtension } from '@/components/editor/extensions/EnhancedTaskItemExtension';
import Mention from '@tiptap/extension-mention';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { MentionList } from '@/components/kb/MentionList';
import { Callout } from '@/components/editor/extensions/Callout';
import { EnhancedTaskListShortcuts } from '@/components/editor/extensions/EnhancedTaskList';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { EnhancedMentionView } from '@/components/editor/extensions/EnhancedMention';
import { EnhancedTaskItemView } from '@/components/editor/extensions/EnhancedTaskItem';

// Mention suggestion configuration
const mentionSuggestion = {
  items: async ({ query }: { query: string }) => {
    try {
      // Use our API client for fetching users
      const { users } = await import('@/lib/kbApi');

      // If no query, get all users (limited to first 10)
      if (!query.trim()) {
        const data = await users.getAll();
        console.log('🔍 [MENTION DEBUG] All users data:', data);
        return (data.users || []).slice(0, 10);
      }

      // Search users by query
      const data = await users.search(query);
      console.log('🔍 [MENTION DEBUG] Search users data:', data);

      return (data.users || [])
        .filter((user: any) =>
          user.name.toLowerCase().includes(query.toLowerCase()) ||
          user.email.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10);
    } catch (error) {
      console.warn('Error fetching mention suggestions:', error);
      return [];
    }
  },

  command: ({ editor, range, props }: any) => {
    // props comes from MentionList component's command() call
    // Debug: Log what we're receiving
    console.log('🔍 [MENTION DEBUG] Inserting mention with props:', props);

    // Ensure we insert mention with complete user identity
    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        {
          type: "mention",
          attrs: {
            id: props.id,           // user id (required)
            label: props.label,     // display name (required)
            email: props.email,     // user email (required for hover lookup)
          },
        },
        { type: "text", text: " " },
      ])
      .run();
  },

  render: () => {
    let component: ReactRenderer<any>;
    let popup: TippyInstance[];

    return {
      onStart: (props: any) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          hideOnClick: 'toggle',
          maxWidth: 300,
          zIndex: 9999,
        });
      },

      onUpdate(props: any) {
        component?.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onKeyDown(props: any) {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }

        return component?.ref?.onKeyDown?.(props) || false;
      },

      onExit() {
        if (popup?.[0]) {
          popup[0].destroy();
          popup = [];
        }
        if (component) {
          component.destroy();
          component = null;
        }
      },
    };
  },
};

// Canonical TipTap configuration for RealtyHaus Knowledge Base
export const getTiptapExtensions = () => {
  try {
    console.log('Loading TipTap extensions...');
    return [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3, 4, 5, 6],
    },
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: {
        class: 'tiptap-bullet-list',
      },
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: {
        class: 'tiptap-ordered-list',
      },
    },
    listItem: {
      HTMLAttributes: {
        class: 'tiptap-list-item',
      },
    },
    strike: false, // Disable StarterKit's strike to avoid duplicate
  }),
  TextStyle,
  Color.configure({
    types: ['textStyle'],
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
  }),
  Underline,
  Strike,
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      class: 'text-primary underline underline-offset-2 hover:text-primary/80 transition-colors',
    },
  }),
  Image.configure({
    HTMLAttributes: {
      class: 'rounded-lg max-w-full h-auto',
    },
  }),
  Placeholder.configure({
    placeholder: ({ node }) => {
      if (node.type.name === 'heading') {
        return 'What\'s the title?';
      }
      return 'Write your article content here...';
    },
  }),
  TaskList.configure({
    HTMLAttributes: {
      class: 'task-list',
    },
  }),
  EnhancedTaskItemExtension.configure({
    HTMLAttributes: {
      class: 'task-item',
    },
    nested: true,
  }),
  Mention.configure({
    HTMLAttributes: {
      class: 'mention bg-primary/10 text-primary px-1 py-0.5 rounded font-medium',
    },
    suggestion: mentionSuggestion,
    renderText({ node }) {
      return `@${node.attrs.label ?? node.attrs.id}`;
    },
    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: element => element.getAttribute('data-user-id'),
          renderHTML: attributes => ({ 'data-user-id': attributes.id }),
        },
        label: {
          default: null,
          parseHTML: element => element.getAttribute('data-label'),
          renderHTML: attributes => ({ 'data-label': attributes.label }),
        },
        email: {
          default: null,
          parseHTML: element => element.getAttribute('data-user-email'),
          renderHTML: attributes => ({ 'data-user-email': attributes.email }),
        },
      };
    },
    addNodeView() {
      return ReactNodeViewRenderer(EnhancedMentionView);
    },
  }),
  Callout,
  EnhancedTaskListShortcuts,
];
  } catch (error) {
    console.error('Error loading TipTap extensions:', error);
    throw error;
  }
};

// Editor attributes for consistent styling
export const tiptapEditorAttributes = {
  class: 'prose-editor focus:outline-none min-h-[500px]',
  spellcheck: 'false',
};

// Generate text excerpt from TipTap content
export function generateExcerpt(content: string): string {
  // Remove HTML tags and get first 150 characters
  const text = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 150 ? text.substring(0, 147) + '...' : text;
}